import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, ShieldCheck, UserMinus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { initials, relTime } from "@/lib/format";
import { errMessage, listUsers, removeUser, updateUser } from "@/lib/api";

export const UsersSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await listUsers());
    } catch (e) {
      toast.error("Could not load the user list", { description: errMessage(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeRole = async (user, role) => {
    if (role === user.role) return;
    setBusyId(user.user_id);
    try {
      await updateUser(user.user_id, { role });
      toast.success(`${user.email} is now ${role === "admin" ? "an admin" : "a viewer"}`, {
        description:
          role === "viewer"
            ? "Their open sessions were signed out, so the new limits apply immediately."
            : "They can now change settings and run exports.",
      });
      load();
    } catch (e) {
      toast.error("Could not change the role", { description: errMessage(e) });
    } finally {
      setBusyId("");
    }
  };

  const revoke = async (user) => {
    setBusyId(user.user_id);
    try {
      await removeUser(user.user_id);
      toast.success(`${user.email} no longer has access`);
      load();
    } catch (e) {
      toast.error("Could not revoke access", { description: errMessage(e) });
    } finally {
      setBusyId("");
    }
  };

  const users = data?.items || [];
  const domains = (data?.allowed_domains || ["gostops.com"]).map((d) => `@${d}`).join(", ");

  return (
    <Card
      className="border-border bg-card shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]"
      data-testid="settings-users-card"
    >
      <CardHeader className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
            <Users className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <CardTitle className="text-[15px] font-semibold tracking-tight">Users &amp; access</CardTitle>
            <CardDescription className="mt-1 text-xs leading-relaxed">
              Anyone with a {domains} Google account can sign in, but new people start as read-only
              viewers. Promote the people who handle payouts to admin.
            </CardDescription>
          </div>
        </div>
        <span
          className="shrink-0 rounded-full border border-border bg-secondary px-2.5 py-1 text-[11px] font-semibold"
          data-testid="settings-users-count"
        >
          {users.length} user{users.length === 1 ? "" : "s"} · {data?.admin_count ?? 0} admin
          {(data?.admin_count ?? 0) === 1 ? "" : "s"}
        </span>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {loading ? (
          <div className="space-y-2" data-testid="settings-users-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : null}

        {!loading && !users.length ? (
          <p className="rounded-lg border border-border bg-secondary p-4 text-xs text-muted-foreground">
            Nobody has signed in yet.
          </p>
        ) : null}

        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {users.map((u) => (
            <li
              key={u.user_id}
              className="flex flex-col gap-3 bg-card p-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`settings-user-${u.email}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-primary">
                  {initials(u.name || u.email)}
                </span>
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-[13px] font-semibold">
                    {u.name || u.email}
                    {u.is_you ? (
                      <span className="rounded-full border border-border bg-secondary px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                        You
                      </span>
                    ) : null}
                    {u.signed_in ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-success-border bg-success-surface px-1.5 py-0.5 text-[9.5px] font-semibold text-success-text">
                        Signed in
                      </span>
                    ) : null}
                  </p>
                  <p className="vde-code truncate text-[11px] text-muted-foreground">
                    {u.email}
                  </p>
                  <p className="mt-0.5 text-[10.5px] text-muted-foreground">
                    last sign-in {relTime(u.last_login_at) || "never"}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Select
                  value={u.role}
                  onValueChange={(role) => changeRole(u, role)}
                  disabled={u.is_you || busyId === u.user_id}
                >
                  <SelectTrigger
                    className={cn("h-9 w-[136px] bg-card text-xs")}
                    data-testid={`settings-user-role-${u.email}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="admin" className="text-xs">
                      <span className="flex items-center gap-1.5">
                        <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Admin
                      </span>
                    </SelectItem>
                    <SelectItem value="viewer" className="text-xs">
                      <span className="flex items-center gap-1.5">
                        <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Viewer
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:bg-danger-surface hover:text-danger-text"
                      disabled={u.is_you || busyId === u.user_id}
                      aria-label={`Revoke access for ${u.email}`}
                      data-testid={`settings-user-revoke-${u.email}`}
                    >
                      {busyId === u.user_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <UserMinus className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Revoke access for {u.email}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        They will be signed out immediately and will need to be re-added the next
                        time they sign in. Nothing they extracted or exported is deleted.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="settings-user-revoke-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => revoke(u)}
                        data-testid="settings-user-revoke-confirm"
                      >
                        Revoke access
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </li>
          ))}
        </ul>

        <p className="text-xs leading-relaxed text-muted-foreground">
          Admins can connect Gmail, change these settings and run exports. Viewers only see the
          payments inbox and the extraction queue. Every sign-in, settings change, Gmail connection
          and export is recorded in the Activity log.
        </p>
      </CardContent>
    </Card>
  );
};
